import * as React from "react";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AsyncTypeahead } from "react-bootstrap-typeahead";
import { AsyncOperation } from "../../models/asyncOperation";
import { monitorOperation } from "../../realtime/middleware";
import { api } from "../../store/api";
import { useAppSelector } from "../../store/hooks";
import { selectSession } from "../../store/sessionSlice";
import Project from "../../models/project";
import FileBlob from "../../models/fileBlob";

export const ExportsRoute = "/settings/export";

export default function ExportsScreen() {
    const dispatch = useDispatch();
    const sessionOperations = useAppSelector(selectSession).operations;

    const [projectSearchResults, setProjectSearchResults] = useState<Project[] | undefined>([]);
    const [selectedProject, setSelectedProject] = useState<Project>();

    const [tagsExportOperation, setTagsExportOperation] = useState<AsyncOperation>();
    const [tagsExportError, setTagsExportError] = useState<string>();

    const [projectExportError, setProjectExportError] = useState<string>();
    const [projectExportLoading, setProjectExportLoading] = useState(false);
    const [projectDownloadUri, setProjectDownloadUri] = useState<FileBlob>();

    const [allDataExportOperation, setAllDataExportOperation] = useState<AsyncOperation>();
    const [allDataExportError, setAllDataExportError] = useState<string>();

    useEffect(() => {
        api.projects.list()
            .then((records) => {
                const sorted = [...records].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
                setProjectSearchResults(sorted);
            })
            .catch((reason) => {
                console.error(reason);
                setProjectExportError("Unable to load projects.");
            });
    }, []);

    const activeTagsExportOperation = tagsExportOperation
        ? sessionOperations?.find((operation) => operation.context === tagsExportOperation.context)
        : undefined;

    const tagsExportDownloadUrl =
        activeTagsExportOperation?.complete === true && activeTagsExportOperation.message?.startsWith("http")
            ? activeTagsExportOperation.message
            : undefined;

    const activeAllDataExportOperation = allDataExportOperation
        ? sessionOperations?.find((operation) => operation.context === allDataExportOperation.context)
        : undefined;

    const allDataExportDownloadUrl =
        activeAllDataExportOperation?.complete === true && activeAllDataExportOperation.message?.startsWith("http")
            ? activeAllDataExportOperation.message
            : undefined;

    const onExportTags = () => {
        setTagsExportError(undefined);
        api.settings.tags
            .exportAll()
            .then((operation) => {
                setTagsExportOperation(operation);
                dispatch(monitorOperation(operation.context));
            })
            .catch((reason) => {
                console.error(reason);
                setTagsExportError("Unable to queue tags export.");
            });
    };

    const onExportAllData = () => {
        setAllDataExportError(undefined);
        api.settings.exports
            .allData()
            .then((operation) => {
                setAllDataExportOperation(operation);
                dispatch(monitorOperation(operation.context));
            })
            .catch((reason) => {
                console.error(reason);
                setAllDataExportError("Unable to queue all data export.");
            });
    };

    const onExportProject = () => {
        if (!selectedProject?.id) {
            setProjectExportError("Please select a project to export.");
            return;
        }

        setProjectExportError(undefined);
        setProjectExportLoading(true);
        setProjectDownloadUri(undefined);

        api.project(selectedProject.id)
            .export()
            .then((uri) => {
                setProjectDownloadUri(uri);
            })
            .catch((reason) => {
                console.error(reason);
                setProjectExportError("Unable to generate project export.");
            })
            .finally(() => {
                setProjectExportLoading(false);
            });
    };

    return (
        <div>
            <div className="d-flex align-items-center mb-3">
                <div>
                    <ul className="breadcrumb">
                        <li className="breadcrumb-item">Settings</li>
                        <li className="breadcrumb-item active">
                            <Link to={ExportsRoute}>Exports</Link>
                        </li>
                    </ul>
                </div>
            </div>
            <div className="row">
                <div className="col-md-6">
                    <div className="card border-0 mb-4">
                        <div className="card-body">
                            <h4 className="mb-3">Tag Export</h4>

                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <button type="button" className="btn btn-outline-success" onClick={onExportTags}>
                                    <i className="fa-solid fa-file-excel me-2"></i>
                                    Export Tags
                                </button>
                            </div>

                            {tagsExportError && (
                                <div className="alert alert-danger mt-3 mb-0">
                                    <strong>Error!</strong> {tagsExportError}
                                </div>
                            )}

                            {activeTagsExportOperation && !tagsExportDownloadUrl && (
                                <p className="mt-3 mb-0">
                                    <i className="fa-solid fa-spin fa-spinner me-2"></i>
                                    {activeTagsExportOperation.message ?? "Export in progress..."}
                                </p>
                            )}

                            {tagsExportDownloadUrl && (
                                <div className="mt-3">
                                    <a href={tagsExportDownloadUrl} className="btn btn-success">
                                        <i className="fa-solid fa-download me-2"></i>
                                        Download Tags Export
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="col-md-6">

                    <div className="card border-0 mb-4">
                        <div className="card-body">
                            <h4 className="mb-3">Project Export</h4>

                            <div className="row g-2 align-items-end">
                                <div className="col-md-8">
                                    <label className="form-label">Project</label>
                                    <AsyncTypeahead
                                        options={projectSearchResults ?? []}
                                        id="projectDropDown"
                                        labelKey={"name"}
                                        filterBy={["name"]}
                                        selected={selectedProject ? [selectedProject] : []}
                                        minLength={0}
                                        isLoading={projectSearchResults === undefined}
                                        clearButton={true}
                                        onSearch={(search: string) => {
                                            setProjectSearchResults(undefined);
                                            api.projects.search({ search }).then((result) => {
                                                setProjectSearchResults(result.records);
                                            })
                                                .catch((reason) => {
                                                    console.error(reason);
                                                    setProjectExportError("Unable to search projects.");
                                                    setProjectSearchResults([]);
                                                });
                                        }}
                                        onChange={(selected) => {
                                            setProjectDownloadUri(undefined);
                                            const selectedProjects = selected as Project[];
                                            if (selectedProjects.length > 0) {
                                                setSelectedProject(selectedProjects[0]);
                                            }
                                            else {
                                                setSelectedProject(undefined);
                                            }
                                        }}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <button type="button" className="btn btn-outline-success w-100" onClick={onExportProject} disabled={projectExportLoading || !selectedProject?.id}>
                                        <i className="fa-solid fa-file-excel me-2"></i>
                                        Export Project
                                    </button>
                                </div>
                            </div>

                            {projectExportError && (
                                <div className="alert alert-danger mt-3 mb-0">
                                    <strong>Error!</strong> {projectExportError}
                                </div>
                            )}

                            {projectExportLoading && (
                                <p className="mt-3 mb-0">
                                    <i className="fa-solid fa-spin fa-spinner me-2"></i>
                                    Generating project export...
                                </p>
                            )}

                            {projectDownloadUri?.url && (
                                <div className="mt-3">
                                    <a href={projectDownloadUri.url} className="btn btn-success">
                                        <i className="fa-solid fa-download me-2"></i>
                                        Download Project Export
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="card border-0 mb-4">
                        <div className="card-body">
                            <h4 className="mb-3">All Data Export</h4>

                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <button type="button" className="btn btn-outline-success" onClick={onExportAllData}>
                                    <i className="fa-solid fa-database me-2"></i>
                                    Export All Data
                                </button>
                            </div>

                            {allDataExportError && (
                                <div className="alert alert-danger mt-3 mb-0">
                                    <strong>Error!</strong> {allDataExportError}
                                </div>
                            )}

                            {activeAllDataExportOperation && !allDataExportDownloadUrl && (
                                <p className="mt-3 mb-0">
                                    <i className="fa-solid fa-spin fa-spinner me-2"></i>
                                    {activeAllDataExportOperation.message ?? "Export in progress..."}
                                </p>
                            )}

                            {allDataExportDownloadUrl && (
                                <div className="mt-3">
                                    <a href={allDataExportDownloadUrl} className="btn btn-success">
                                        <i className="fa-solid fa-download me-2"></i>
                                        Download All Data Export
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>


        </div>
    );
}
